import type { Lead } from '@/types/leads';
import type { OutreachTemplateKey } from '@/types/lead-outreach';

/** Canonical pain ids from leads.solvable_pain_points (Jeff lead engine). */
export const LEAD_PAIN_IDS = [
  'PAIN-MENU',
  'PAIN-THROUGHPUT',
  'PAIN-DISCOVERY',
  'PAIN-LOYALTY',
  'PAIN-FULFILLMENT',
  'PAIN-EXPERIENCE',
  'PAIN-INSIGHT',
] as const;

export type LeadPainId = (typeof LEAD_PAIN_IDS)[number];

export const painAngleLabels: Record<LeadPainId, string> = {
  'PAIN-MENU': 'Live menu on the sales floor',
  'PAIN-THROUGHPUT': 'Move shopping off the counter',
  'PAIN-DISCOVERY': 'Discovery and basket lift',
  'PAIN-LOYALTY': 'Floor behavior for repeat customers',
  'PAIN-FULFILLMENT': 'In-store order handoff',
  'PAIN-EXPERIENCE': 'Interactive floor differentiation',
  'PAIN-INSIGHT': 'Visibility before checkout',
};

const PAIN_TO_TEMPLATE: Record<LeadPainId, OutreachTemplateKey> = {
  'PAIN-DISCOVERY': 'A1_basket',
  'PAIN-MENU': 'A1_menu',
  'PAIN-THROUGHPUT': 'A1_throughput',
  'PAIN-EXPERIENCE': 'A1_experience',
  'PAIN-LOYALTY': 'A1_loyalty',
  'PAIN-FULFILLMENT': 'A1_fulfillment',
  'PAIN-INSIGHT': 'A1_insight',
};

const BRAND_KEYWORDS = [
  'brand',
  'vendor',
  'spotlight',
  'featured',
  'placement',
  'co-op',
  'coop',
  'ad-click',
  'ad click',
  'co-op dollar',
];

const PAIN_SIGNAL_HINTS: Array<{ pain: LeadPainId; keywords: string[] }> = [
  { pain: 'PAIN-MENU', keywords: ['menu', 'sold out', 'stock', 'inaccurate', 'stale menu'] },
  { pain: 'PAIN-THROUGHPUT', keywords: ['line', 'wait', 'understaffed', 'busy', 'checkout'] },
  { pain: 'PAIN-DISCOVERY', keywords: ['overwhelm', 'product info', 'budtender', 'education', 'browse'] },
  { pain: 'PAIN-LOYALTY', keywords: ['reward', 'loyalty', 'repeat', 'never hear'] },
  { pain: 'PAIN-FULFILLMENT', keywords: ['pickup', 'order wasn', 'online order', 'delivery'] },
  { pain: 'PAIN-EXPERIENCE', keywords: ['outdated', 'dated', 'no screen', 'no kiosk', 'clinical'] },
  { pain: 'PAIN-INSIGHT', keywords: ['analytics', 'data', 'visibility', 'behavior'] },
];

function normalizePainId(value: string): LeadPainId | null {
  const upper = value.trim().toUpperCase();
  if ((LEAD_PAIN_IDS as readonly string[]).includes(upper)) {
    return upper as LeadPainId;
  }
  return null;
}

function combinedLeadText(lead: Lead): string {
  return [
    ...(lead.solvable_pain_points ?? []),
    ...(lead.pain_signals ?? []),
    lead.notes ?? '',
    lead.qa_notes ?? '',
  ]
    .join(' ')
    .toLowerCase();
}

export function suggestsBrandAngle(lead: Lead): boolean {
  const text = combinedLeadText(lead);
  return BRAND_KEYWORDS.some((keyword) => text.includes(keyword));
}

export function inferPainFromSignals(lead: Lead): LeadPainId | null {
  const text = combinedLeadText(lead);
  for (const hint of PAIN_SIGNAL_HINTS) {
    if (hint.keywords.some((keyword) => text.includes(keyword))) {
      return hint.pain;
    }
  }
  return null;
}

/** First canonical pain on the lead, else infer from pain_signals / notes. */
export function getPrimaryPainId(lead: Lead): LeadPainId | null {
  for (const raw of lead.solvable_pain_points ?? []) {
    const pain = normalizePainId(raw);
    if (pain) return pain;
  }
  return inferPainFromSignals(lead);
}

export function getPainAngleLabel(painId: LeadPainId | null): string | null {
  if (!painId) return null;
  return painAngleLabels[painId];
}

export function templateForPainId(painId: LeadPainId): OutreachTemplateKey {
  return PAIN_TO_TEMPLATE[painId];
}

export function suggestPinchHitTemplateFromPains(lead: Lead): OutreachTemplateKey {
  if (suggestsBrandAngle(lead) && !getPrimaryPainId(lead)) {
    return 'A2_brand';
  }

  const primaryPain = getPrimaryPainId(lead);
  if (primaryPain) {
    if (suggestsBrandAngle(lead) && primaryPain === 'PAIN-DISCOVERY') {
      return 'A2_brand';
    }
    return templateForPainId(primaryPain);
  }

  if (suggestsBrandAngle(lead)) return 'A2_brand';
  return 'A1_basket';
}

export function getIncumbentVendorLabel(lead: Lead): string | null {
  const incumbent = lead.incumbent_instore_tech;
  if (!incumbent || incumbent.status !== 'competitor' || !incumbent.vendor?.trim()) {
    return null;
  }
  return incumbent.vendor.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function hasCompetitorIncumbent(lead: Lead): boolean {
  return lead.incumbent_instore_tech?.status === 'competitor';
}
